import React, { Component, Fragment } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment';
import { Popover } from 'antd';
import { observer } from 'mobx-react';
import { buttonNormal } from '../../../constants/themes';
import InfoRow from '../../common/InfoRow';
import Radio from '../../common/Radio';
import { text2, text3 } from '../../../constants/classes';
import { arrowUp, arrowDown } from '../../../constants/classes/arrow';
import { CollapseButton } from '../../common/SingleItem';
import Input from '../../common/Input';
import { InfoRowContainer, Label, Separator } from '../../common/styled';
import Dropdown from '../../common/Dropdown';
import { statusList, monitoringStatusList } from '../../../constants/listings';
import Labels from './Labels';
import currencies from '../../../constants/currency.json';
import { formatItemUrl } from '../../../utils/formatMarketsUrl';
import PricingEditContainer from './PricingEditContainer';
import IconSvg from '../../common/IconSvg';
import ItemSpecifics from '../../common/ItemSpecifics';

const { Option } = Dropdown;
const RadioGroup = Radio.Group;

const Unit = styled.span`
  padding-right: 8px;
  width: 23px;
`;

@injectIntl
@observer
export default class ItemInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showMore: false,
      asinActive: false,
      newAsin: '',
      showSaveAsin: false,
      sourceItemCodeChanged: props.data.sourceItemCodeChanged,
      salePriceActive: !props.data.multiVariation,
      breakEvenProfitActive: props.data.multiVariation,
      initialAsin: props.data.sourceItemCode ? props.data.sourceItemCode : null,
      initialSalePrice: props.data.pricingStrategy ? props.data.pricingStrategy.salePrice : null,
      initialBreakEven: props.data.pricingStrategy
        ? props.data.pricingStrategy.strategyBreakEven
        : null,
      initialProfitInPercents: props.data.pricingStrategy
        ? props.data.pricingStrategy.profitInPercents
        : null,
      initialProfit: props.data.pricingStrategy ? props.data.pricingStrategy.profit : null,
      initialStrategyInPercents: props.data.pricingStrategy
        ? props.data.pricingStrategy.strategyInPercents
        : null,
      initialStatus: props.data.status,
      initialQuantity: props.data.quantity,
      initialMonitoringStatus: props.data.monitoringStatus,
      initialLabels: props.data.labels.length,
      initialTemplate: props.data.templateId,
      lastEdit: null,
    };
    this.updateLastEdit = this.updateLastEdit.bind(this);
    this.toggleShowMore = this.toggleShowMore.bind(this);
    this.handleValueProfitChange = this.handleValueProfitChange.bind(this);
    this.handleStrategyInPercents = this.handleStrategyInPercents.bind(this);
    this.onSaveNewAsin = this.onSaveNewAsin.bind(this);
    this.props.onRef(this);
  }

  onAsinEditEnable = () => {
    const { initialAsin, asinActive } = this.state;
    if (asinActive) {
      const {
        data: { setAsin },
      } = this.props;
      setAsin(initialAsin);
    }
    this.setState(() => ({ asinActive: !asinActive, newAsin: '', showSaveAsin: false }));
  };

  onSalePriceEditEnable = () => {
    const { initialBreakEven, initialProfitInPercents, initialProfit } = this.state;
    const {
      cloneIsEqual,
      data: {
        pricingStrategy: {
          strategyBreakEven,
          profitInPercents,
          profit,
          strategyInPercents,
          initialStrategyInPercents,
        },
      },
    } = this.props;
    const newState = {
      salePriceActive: true,
      breakEvenProfitActive: false,
    };
    if (
      cloneIsEqual &&
      (initialBreakEven !== strategyBreakEven ||
        initialProfitInPercents !== profitInPercents ||
        initialProfit !== profit ||
        initialStrategyInPercents !== strategyInPercents)
    ) {
      newState.initialBreakEven = strategyBreakEven;
      newState.initialProfitInPercents = profitInPercents;
      newState.initialProfit = profit;
      newState.initialStrategyInPercents = strategyInPercents;
    }
    this.setState(newState, this.resetBreakEvenProfit);
  };

  onBreakEvenProfitEditEnable = () => {
    const { initialSalePrice } = this.state;
    const {
      cloneIsEqual,
      data: {
        pricingStrategy: { salePrice },
      },
    } = this.props;
    const newState = {
      salePriceActive: false,
      breakEvenProfitActive: true,
    };
    if (cloneIsEqual && initialSalePrice !== salePrice) {
      newState.initialSalePrice = salePrice;
    }
    this.setState(newState, this.resetSalePrice);
  };

  onSaveNewAsin() {
    const { newAsin } = this.state;
    const {
      handleItemAsinUpdate,
      data: { setAsin },
    } = this.props;
    setAsin(newAsin);
    handleItemAsinUpdate();
  }

  handleValueProfitChange(e) {
    const { data } = this.props;
    data.setProfit(e.target.value);
  }

  handleStrategyInPercents(e) {
    const { data } = this.props;
    data.setStrategyPercents(e.target.value);
  }

  resetSalePrice = () => {
    const {
      data: { setDestinationPrice },
    } = this.props;
    const { initialSalePrice } = this.state;
    setDestinationPrice(initialSalePrice);
  };

  resetBreakEvenProfit = () => {
    const {
      data: { setUnitProfit, setPercentProfit, setStrategyPercents, setBreakEven },
    } = this.props;
    const {
      initialBreakEven,
      initialProfitInPercents,
      initialProfit,
      initialStrategyInPercents,
    } = this.state;
    setBreakEven(initialBreakEven);
    setStrategyPercents(initialStrategyInPercents);
    setPercentProfit(initialProfitInPercents);
    setUnitProfit(initialProfit);
  };

  toggleShowMore() {
    this.setState(({ showMore }) => ({ showMore: !showMore }));
  }

  typeNewAsin(value) {
    const { initialAsin } = this.state;
    this.setState(() => ({ showSaveAsin: value !== initialAsin && value, newAsin: value }));
  }

  refreshAsinActive() {
    this.setState(() => ({
      asinActive: false,
      initialAsin: this.props.data.sourceItemCode,
      newAsin: '',
      sourceItemCodeChanged: this.props.data.sourceItemCodeChanged,
    }));
  }

  updateLastEdit = (e, idParam, applyChanges = null) => {
    if (e !== null) {
      e.persist();
    }
    this.setState({
      lastEdit: { e, idParam, applyChanges },
    });

    console.log('aici',this.state.lastEdit);
  };

  render() {
    const {
      showMore,
      salePriceActive,
      breakEvenProfitActive,
      asinActive,
      showSaveAsin,
      newAsin,
      sourceItemCodeChanged,
      initialStatus,
      initialSalePrice,
      initialBreakEven,
      initialProfitInPercents,
      initialProfit,
      initialStrategyInPercents,
      initialQuantity,
      initialMonitoringStatus,
      initialLabels,
      initialTemplate,
    } = this.state;
    const { intl, data } = this.props;
    const sourceUrl = formatItemUrl(data.sourceItemCode, data.sourceMarketplaceId);
    const initialSourceUrl = formatItemUrl(data.initialSourceItemCode, data.sourceMarketplaceId);

    const popoverContent = (
      <div
        css={css`
          color: white;
        `}
      >
        To finish this update save and use the chrome extension.
      </div>
    );

    const infoRocket = (
      <span>
        <Popover
          content={popoverContent}
          title=""
          overlayClassName="ant-popover-background"
          trigger="hover"
          placement="right"
        >
          <span href="">
            <IconSvg type="horizontal_ship" height={20} />
          </span>
        </Popover>
      </span>
    );

    return (
      <Fragment>
        <InfoRowContainer>
          <div
            css={css`
              width: 212px;
            `}
          >
            <Label>
              <strong>
                {data.sourceMarketplaceId === 'AZ_US'
                  ? intl.formatMessage({ id: 'codes.asin' })
                  : intl.formatMessage({ id: 'codes.marketId' })}
              </strong>
              :&nbsp;
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={sourceUrl}
                css={css`
                  ${text3};
                  text-decoration: underline;
                `}
              >
                {data.sourceItemCode}
              </a>
            </Label>
            {!asinActive && (
              <IconSvg
                onClick={this.onAsinEditEnable}
                css={css`
                  margin-left: 5px;
                `}
                type="pencil"
                color={buttonNormal}
                size={15}
              />
            )}
          </div>
          <Label>
            <strong>UPC:</strong> {data.upc}
          </Label>
        </InfoRowContainer>
        {sourceItemCodeChanged && (
          <InfoRowContainer>
            <div
              css={css`
                width: 500px;
                margin-top: -18px;
              `}
            >
              <Label
                css={css`
                  font-size: 11px;
                `}
              >
                {data.sourceMarketplaceId === 'AZ_US'
                  ? intl.formatMessage({ id: 'listingsPage.asinWasChanged' })
                  : intl.formatMessage({ id: 'listingsPage.marketIdWasChanged' })}
                :&nbsp;
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href={initialSourceUrl}
                  css={css`
                    ${text3};
                    text-decoration: underline;
                    font-size: 11px;
                  `}
                >
                  {data.initialSourceItemCode}
                </a>
              </Label>
            </div>
          </InfoRowContainer>
        )}
        {asinActive && (
          <InfoRowContainer>
            <div
              css={css`
                width: 400px;
              `}
            >
              <Label>
                <strong>
                  New{' '}
                  {data.sourceMarketplaceId === 'AZ_US'
                    ? intl.formatMessage({ id: 'codes.asin' })
                    : intl.formatMessage({ id: 'codes.marketId' })}
                </strong>
                :&nbsp;
                <Input
                  value={newAsin}
                  width="135px"
                  type="text"
                  onChange={e => this.typeNewAsin(e.target.value)}
                />
              </Label>
              {showSaveAsin && (
                <IconSvg
                  onClick={this.onSaveNewAsin}
                  css={css`
                    margin-left: 5px;
                  `}
                  type="v"
                  color={buttonNormal}
                  size={15}
                />
              )}
              <IconSvg
                onClick={this.onAsinEditEnable}
                css={css`
                  margin-left: 5px;
                `}
                type="x"
                color={buttonNormal}
                size={15}
              />
            </div>
          </InfoRowContainer>
        )}
        <InfoRow titleId="listingsPage.viewInDest">
          <a
            rel="noopener noreferrer"
            target="_blank"
            href={data.destinationItemUrl}
            css={css`
              ${text3};
              text-decoration: underline;
            `}
          >
            {data.destinationItemId}
          </a>
        </InfoRow>
        <InfoRow titleId="common.sourceMarketplace" value={data.marketplaceName} />
        <InfoRow titleId="common.account" value={data.sellerAccountName} />
        <InfoRow titleId="listingsPage.listingStatus">
          <Dropdown value={data.status} dropdownMatchSelectWidth={false} onChange={data.setStatus}>
            {statusList
              .filter(
                ({ status }) =>
                  data.status !== 'ended' || (data.status === 'ended' && status !== 'active'),
              )
              .map(({ status }) => (
                <Option value={status} key={status}>
                  <FormattedMessage id={`listingsPage.${status}`} />
                </Option>
              ))}
          </Dropdown>
          {data.isNonApi && initialStatus !== data.status.toLowerCase() && (
            <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center' }}>
              {infoRocket}
            </div>
          )}
        </InfoRow>
        <InfoRow titleId="listingsPage.listDate" style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <span
              css={css`
                ${text3}
              `}
            >
              {moment(data.listDate).format('YYYY-MM-DD')}
            </span>
          </div>
        </InfoRow>
        <InfoRow
          titleId="listingsPage.isMarketplace"
          style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}
        >
          <div>
            <span
              css={css`
                ${text3}
              `}
            >
              {data.isMarketplace ? 'True' : 'False'}
            </span>
          </div>
        </InfoRow>
        <InfoRow titleId="common.sourcePrice">
          <Unit>{currencies[data.pricingStrategy.currency].symbol}</Unit>
          <div>
            <span
              css={css`
                ${text3}
              `}
              id="single-listing-source-price"
            >
              {data.pricingStrategy.sourcePrice}
            </span>
          </div>
        </InfoRow>
        <PricingEditContainer
          active={salePriceActive}
          onEditButtonClick={this.onSalePriceEditEnable}
        >
          <InfoRow titleId="common.destinationPrice">
            <Unit>{currencies[data.pricingStrategy.currency].symbol}</Unit>
            <div>
              <Input
                id="single-listing-sale-price"
                value={data.pricingStrategy.salePrice}
                width="70px"
                type="number"
                step="0.0000001"
                required
                onChange={e => data.setDestinationPrice(e.target.value)}
                disabled={!!data.multiVariation || !salePriceActive}
              />
            </div>
            {data.isNonApi && initialSalePrice !== data.pricingStrategy.salePrice && (
              <div>{infoRocket}</div>
            )}
          </InfoRow>
        </PricingEditContainer>
        <PricingEditContainer
          active={breakEvenProfitActive}
          onEditButtonClick={this.onBreakEvenProfitEditEnable}
        >
          <InfoRow titleId="common.breakEven">
            <Unit>%</Unit>
            <div>
              <Input
                id="single-listing-break-even"
                value={data.pricingStrategy.strategyBreakEven}
                width="70px"
                type="number"
                required
                onChange={e => data.setBreakEven(e.target.value)}
                disabled={!breakEvenProfitActive}
              />
            </div>
            {data.isNonApi && initialBreakEven !== data.pricingStrategy.strategyBreakEven && (
              <div>{infoRocket}</div>
            )}
          </InfoRow>
          <InfoRow titleId="common.profit" style={{ width: '500px' }}>
            <Unit>
              {data.pricingStrategy.strategyInPercents
                ? '%'
                : currencies[data.pricingStrategy.currency].symbol}
            </Unit>
            <div>
              {data.pricingStrategy.strategyInPercents ? (
                <Input
                  id="single-listing-profit-in-percents"
                  key="profit-in-percents"
                  value={data.pricingStrategy.profitInPercents}
                  width="70px"
                  css={css`
                    margin-right: 10px;
                  `}
                  type="number"
                  required
                  onChange={this.handleValueProfitChange}
                  disabled={!breakEvenProfitActive}
                />
              ) : (
                <Input
                  id="single-listing-profit-in-units"
                  key="profit-in-units"
                  value={data.pricingStrategy.profit}
                  width="70px"
                  css={css`
                    margin-right: 10px;
                  `}
                  type="number"
                  required
                  onChange={this.handleValueProfitChange}
                  disabled={!breakEvenProfitActive}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '30px' }}>
              <RadioGroup
                value={data.pricingStrategy.strategyInPercents}
                onChange={this.handleStrategyInPercents}
                disabled={!breakEvenProfitActive}
              >
                <Radio id="single-listing-units" value={false}>
                  {currencies[data.pricingStrategy.currency].symbol}
                </Radio>
                <Radio id="single-listing-percent" value>
                  %
                </Radio>
              </RadioGroup>
              {data.isNonApi &&
                (initialProfitInPercents !== data.pricingStrategy.profitInPercents ||
                  initialProfit !== data.pricingStrategy.profit ||
                  initialStrategyInPercents !== data.pricingStrategy.strategyInPercents) && (
                  <div>{infoRocket}</div>
                )}
            </div>
          </InfoRow>
        </PricingEditContainer>
        <Separator />
        <div
          css={css`
            display: flex;
          `}
        >
          <InfoRow
            titleId="common.qty"
            value={data.quantity}
            inputWidth="70px"
            type="number"
            editable
            required
            onChange={e => data.setQuantity(e.target.value)}
            disabled={!!data.multiVariation}
          />
          {data.isNonApi && initialQuantity !== data.quantity && <div>{infoRocket}</div>}
        </div>
        <InfoRow titleId="listingsPage.monitoringStatus">
          <Dropdown
            value={data.monitoringStatus}
            dropdownMatchSelectWidth={false}
            onChange={data.setMonitoringStatus}
          >
            {monitoringStatusList.map(({ monitoringStatus }) => (
              <Option value={monitoringStatus} key={monitoringStatus}>
                <FormattedMessage id={`listingsPage.${monitoringStatus}`} />
              </Option>
            ))}
          </Dropdown>
          {data.isNonApi && initialMonitoringStatus !== data.monitoringStatus && (
            <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center' }}>
              {infoRocket}
            </div>
          )}
        </InfoRow>
        {/* <InfoRow titleId="listingsPage.quantitySold" value={data.quantitySold} /> */}
        <Separator />
        <InfoRow titleId="common.labels" width="60%" nonFixedHeight>
          <Labels labels={data.labels} addLabel={data.addLabel} removeLabel={data.removeLabel} />
          {data.isNonApi && initialLabels !== data.labels.length && (
            <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center' }}>
              {infoRocket}
            </div>
          )}
        </InfoRow>

        {showMore && (
          <Fragment>
            <InfoRow titleId="listingsPage.category" value={data.categoryName} />
            <InfoRow titleId="listingsPage.template">
              <Dropdown
                value={data.getTemplateName(data.templateId)}
                width="110px"
                dropdownMatchSelectWidth={false}
                onChange={data.setTemplate}
              >
                {data.templateList.map(({ id, name }) => (
                  <Option value={id} key={name}>
                    {name}
                  </Option>
                ))}
              </Dropdown>
              {data.isNonApi && initialTemplate !== data.templateId && (
                <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center' }}>
                  {infoRocket}
                </div>
              )}
            </InfoRow>
            <InfoRow titleId="listingsPage.daysWithoutSale" value={data.withoutSalesDays} />
            <ItemSpecifics
              formatMessage={intl.formatMessage}
              specificsData={data.specifics}
              itemId={data.id}
              updateSpecifics={data.addItemSpecific}
              setSpinner={data.setSpinner}
              onChange={data.updateSpecific}
              updateLastEdit={this.updateLastEdit}
              isListingEdit
              deleteItemSpecific={data.deleteItemSpecific}
              propertyWasOptimized={()=>{}}
              propertyIsVero={()=>{}}
              isSpecificUpdating={data.isSpecificUpdating}
            />
            {/* {data.specifics.length ? (
              <Fragment>
                <Separator />
                <InfoRow titleId="common.itemSpecifics" bold />
                {data.specifics.map(({ name, value }) => (
                  <InfoRow key={name} title={name} value={value} />
                ))}
                <Separator />
              </Fragment>
            ) : null} */}
            <InfoRow titleId="common.condition" value={data.condition} />
            <InfoRow titleId="listingsPage.timeLeft" value={data.timeLeftDays} />
            <InfoRow
              titleId="listingsPage.endDate"
              value={moment(data.endDate).format('YYYY-MM-DD')}
            />
            <InfoRow
              titleId="listingsPage.withVariations"
              value={
                data.multiVariation
                  ? intl.formatMessage({ id: 'common.yes' })
                  : intl.formatMessage({ id: 'common.no' })
              }
            />
          </Fragment>
        )}
        <CollapseButton
          css={css`
            ${text2};
            ${showMore ? arrowUp : arrowDown}
            width: auto;
          `}
          onClick={this.toggleShowMore}
        >
          {intl.formatMessage({ id: showMore ? 'orders.showLess' : 'orders.showMore' })}
        </CollapseButton>
        <Separator />
      </Fragment>
    );
  }
}

ItemInfo.propTypes = {
  data: PropTypes.object,
  intl: PropTypes.object,
  cloneIsEqual: PropTypes.bool,
};
